import { useEffect, useState, useCallback } from "react";
import { Reorder } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, GripVertical, ListOrdered, ListPlus, Layers, Lock, Unlock, Pencil, FileText, Copy, Check, ChevronUp, ChevronDown, Clock, Camera, Video } from "lucide-react";

const TOKEN_LABELS = { index: "Index", event_code: "Event code", section: "Section", performance_number: "Perf number", studio: "Studio", name: "Name" };
const ALL_TOKENS = ["index", "event_code", "section", "performance_number", "studio", "name"];

export default function AdminPerformances() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("editor"); // editor | files

  useEffect(() => {
    api.get("/admin/events").then(({ data }) => setEvents(data.filter((e) => e.category === "dance_competition"))).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!eventId) return;
    setLoading(true);
    api.get(`/admin/events/${eventId}/manager`).then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const locked = !!data?.order_locked;
  const sections = data?.sections || [];
  const perfs = data?.performances || [];
  const perfsFor = (sid) => perfs.filter((p) => (p.section_id || "") === (sid || ""));
  const fields = data?.fields || {};
  const flexible = data?.competitor_mode === "flexible";

  const patchEvent = async (body) => {
    setData((d) => ({ ...d, ...body }));
    try { await api.put(`/admin/events/${eventId}`, body); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };
  const toggleLock = () => patchEvent({ order_locked: !locked });

  const addSection = async (names) => {
    const arr = names.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!arr.length) return;
    try { await api.post(`/admin/events/${eventId}/sections`, arr.length > 1 ? { names: arr } : { name: arr[0] }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const updSection = async (sid, body) => { try { await api.put(`/admin/events/${eventId}/sections/${sid}`, body); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const delSection = async (sid) => { if (!window.confirm("Delete this section? Its performances become unassigned.")) return; await api.delete(`/admin/events/${eventId}/sections/${sid}`); load(); };
  const reorderSections = async (ordered) => {
    if (locked) return;
    setData((d) => ({ ...d, sections: ordered }));
    try { await api.put(`/admin/events/${eventId}/sections/reorder`, { ids: ordered.map((s) => s.id) }); load(); } catch { load(); }
  };

  const addPerf = async (sectionId, body) => {
    try { const { data: r } = await api.post(`/admin/events/${eventId}/performances`, { section_id: sectionId, ...body }); if (r.skipped) toast.info(`${r.skipped} duplicate(s) skipped`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const updPerf = async (pid, body, silent) => { try { await api.put(`/admin/events/${eventId}/performances/${pid}`, body); if (!silent) load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); } };
  const delPerf = async (pid) => { if (!window.confirm("Delete this performance and its linked media?")) return; await api.delete(`/admin/events/${eventId}/performances/${pid}`); load(); };
  const reorderPerfs = async (sid, ordered) => {
    if (locked) return;
    const others = perfs.filter((p) => (p.section_id || "") !== (sid || ""));
    const items = ordered.map((p, i) => ({ id: p.id, section_id: sid || null, order: i }));
    setData((d) => ({ ...d, performances: [...others, ...ordered.map((p, i) => ({ ...p, order: i }))] }));
    try { await api.put(`/admin/events/${eventId}/performances/reorder`, { items }); load(); } catch { load(); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono-x text-xs uppercase tracking-widest text-zinc-500">Competitions</div>
          <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">Performance manager</h1>
        </div>
        <div className="min-w-[260px]">
          <Select value={eventId} onValueChange={setEventId}>
            <SelectTrigger data-testid="pm-event-select" className="border-zinc-700 bg-zinc-950 text-white"><SelectValue placeholder="Select a competition…" /></SelectTrigger>
            <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
              {events.length === 0 ? <div className="px-3 py-2 text-sm text-zinc-500">No competition products yet</div>
                : events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!eventId ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 py-20 text-center text-zinc-500">
          <ListOrdered className="mx-auto mb-3 h-8 w-8" /> Select a dance competition to manage its sections and performances.
        </div>
      ) : loading || data === null ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#ff2a5f]" /></div>
      ) : (
        <div className="space-y-5">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-zinc-800 bg-[#0d0d10] p-3">
            <div className="inline-flex rounded-lg border border-zinc-700 p-0.5">
              <button data-testid="pm-view-editor" onClick={() => setView("editor")} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${view === "editor" ? "bg-[#ff2a5f] text-white" : "text-zinc-400 hover:text-white"}`}><Pencil className="h-3.5 w-3.5" /> Editor</button>
              <button data-testid="pm-view-files" onClick={() => setView("files")} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${view === "files" ? "bg-[#ff2a5f] text-white" : "text-zinc-400 hover:text-white"}`}><FileText className="h-3.5 w-3.5" /> File Manager</button>
              <button data-testid="pm-view-tracker" onClick={() => setView("tracker")} className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${view === "tracker" ? "bg-[#ff2a5f] text-white" : "text-zinc-400 hover:text-white"}`}><Clock className="h-3.5 w-3.5" /> Performance Tracker</button>
            </div>
            <button data-testid="pm-lock-toggle" onClick={toggleLock} className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium ${locked ? "border-amber-500/40 bg-amber-500/15 text-amber-400" : "border-zinc-700 text-zinc-300 hover:text-white"}`}>
              {locked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />} {locked ? "Unlock Performances" : "Lock Performances"}
            </button>
            <label className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-1.5 text-sm text-zinc-300">
              <Checkbox data-testid="pm-prefix-toggle" checked={!!data.prefix_enabled} onCheckedChange={(c) => patchEvent({ prefix_enabled: !!c })} className="border-zinc-600" /> Use prefixes
            </label>
            <div className="flex items-center gap-1.5">
              <span className="text-xs uppercase text-zinc-500">Event code</span>
              <Input data-testid="pm-event-code" defaultValue={data.event_code} onBlur={(e) => e.target.value !== data.event_code && patchEvent({ event_code: e.target.value })} placeholder="e.g. NDC" className="h-8 w-24 border-zinc-800 bg-zinc-950 text-sm text-white" />
            </div>
            <FilenameFormat format={data.filename_format || ALL_TOKENS} onChange={(fmt) => patchEvent({ filename_format: fmt })} sample={perfs[0]} />
            <span className="ml-auto text-xs text-zinc-500">Competitor #: {flexible ? "flexible" : "fixed"} · Content: {(data.content_types || []).join(", ") || "none"}</span>
          </div>

          <AddSection onAdd={addSection} />

          <Reorder.Group axis="y" values={sections} onReorder={reorderSections} className="space-y-4">
            {sections.map((s) => (
              <Reorder.Item key={s.id} value={s} dragListener={!locked} className="rounded-xl border border-zinc-800 bg-[#0d0d10]">
                <SectionCard section={s} perfs={perfsFor(s.id)} sections={sections} fields={fields} flexible={flexible} locked={locked} view={view}
                  onUpdate={updSection} onDelete={() => delSection(s.id)} onAddPerf={addPerf} onUpdPerf={updPerf} onDelPerf={delPerf}
                  onReorderPerfs={(ord) => reorderPerfs(s.id, ord)} onMovePerf={(pid, sid) => updPerf(pid, { section_id: sid || null })} />
              </Reorder.Item>
            ))}
          </Reorder.Group>

          {perfsFor("").length > 0 && (
            <div className="rounded-xl border border-dashed border-zinc-700 bg-[#0d0d10] p-4">
              <div className="mb-2 font-mono-x text-[11px] uppercase tracking-widest text-zinc-500">Unassigned</div>
              <PerfList perfs={perfsFor("")} sections={sections} fields={fields} flexible={flexible} locked={locked} view={view}
                onReorder={(ord) => reorderPerfs("", ord)} onUpd={updPerf} onDel={delPerf} onMove={(pid, sid) => updPerf(pid, { section_id: sid || null })} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FilenameFormat({ format, onChange, sample }) {
  const [fmt, setFmt] = useState(format);
  useEffect(() => { setFmt(format); }, [format.join(",")]);
  const toggle = (t) => { const next = fmt.includes(t) ? fmt.filter((x) => x !== t) : [...fmt, t]; setFmt(next); onChange(next); };
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= fmt.length) return; const next = [...fmt]; [next[i], next[j]] = [next[j], next[i]]; setFmt(next); onChange(next); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="pm-fmt-toggle" className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-1.5 text-sm text-zinc-300 hover:text-white"><FileText className="h-3.5 w-3.5" /> Filename format</button>
      </PopoverTrigger>
      <PopoverContent className="w-72 border-zinc-800 bg-zinc-900 text-white">
        <div className="mb-2 text-xs uppercase tracking-wide text-zinc-500">Drag order with arrows · toggle parts</div>
        <div className="space-y-1.5">
          {fmt.map((t, i) => (
            <div key={t} data-testid={`fmt-token-${t}`} className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1">
              <Checkbox checked onCheckedChange={() => toggle(t)} className="border-zinc-600" />
              <span className="flex-1 text-sm">{TOKEN_LABELS[t]}</span>
              <button onClick={() => move(i, -1)} className="text-zinc-500 hover:text-white"><ChevronUp className="h-3.5 w-3.5" /></button>
              <button onClick={() => move(i, 1)} className="text-zinc-500 hover:text-white"><ChevronDown className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {ALL_TOKENS.filter((t) => !fmt.includes(t)).map((t) => (
            <div key={t} className="flex items-center gap-2 rounded-md border border-zinc-800 px-2 py-1 opacity-60">
              <Checkbox checked={false} onCheckedChange={() => toggle(t)} className="border-zinc-600" />
              <span className="flex-1 text-sm text-zinc-500">{TOKEN_LABELS[t]}</span>
            </div>
          ))}
        </div>
        {sample && <div className="mt-3 break-all rounded-md bg-zinc-950 p-2 font-mono-x text-[11px] text-emerald-400">{sample.filename}</div>}
      </PopoverContent>
    </Popover>
  );
}

function AddSection({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  return (
    <div className="rounded-xl border border-zinc-800 bg-[#0d0d10] p-3">
      {!open ? (
        <Button data-testid="pm-add-section" onClick={() => setOpen(true)} variant="outline" className="border-dashed border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800">
          <Layers className="mr-1.5 h-4 w-4" /> Add section(s)
        </Button>
      ) : (
        <div className="space-y-2">
          <Label className="text-xs uppercase text-zinc-500">One section name per line (bulk supported)</Label>
          <textarea data-testid="pm-section-input" value={val} onChange={(e) => setVal(e.target.value)} rows={3} placeholder={"Section A\nSection B"} className="w-full rounded-md border border-zinc-700 bg-zinc-950 p-2 text-sm text-white outline-none focus:border-zinc-500" />
          <div className="flex gap-2">
            <Button data-testid="pm-section-save" onClick={() => { onAdd(val); setVal(""); setOpen(false); }} className="bg-[#ff2a5f] text-white hover:bg-[#e01f50]"><Plus className="mr-1 h-4 w-4" /> Add</Button>
            <Button variant="ghost" onClick={() => { setOpen(false); setVal(""); }} className="text-zinc-400">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionCard({ section, perfs, sections, fields, flexible, locked, view, onUpdate, onDelete, onAddPerf, onReorderPerfs, onUpdPerf, onDelPerf, onMovePerf }) {
  const [name, setName] = useState(section.name);
  const [prefix, setPrefix] = useState(section.prefix || "");
  const [code, setCode] = useState(section.code || "");
  useEffect(() => { setName(section.name); setPrefix(section.prefix || ""); setCode(section.code || ""); }, [section.id]);
  return (
    <div className="p-4">
      <div className="flex flex-wrap items-center gap-2">
        {!locked && <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-zinc-600" />}
        <Input data-testid={`pm-section-name-${section.id}`} value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name !== section.name && onUpdate(section.id, { name })} className="h-8 max-w-[200px] border-zinc-800 bg-zinc-950 font-display font-bold text-white" />
        {view === "editor" && (<>
        <div className="flex items-center gap-1.5">
          <span className="text-xs uppercase text-zinc-500">Code</span>
          <Input data-testid={`pm-section-code-${section.id}`} value={code} onChange={(e) => setCode(e.target.value)} onBlur={() => code !== (section.code || "") && onUpdate(section.id, { code })} placeholder="S22" className="h-8 w-20 border-zinc-800 bg-zinc-950 text-sm text-white" />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs uppercase text-zinc-500">Prefix</span>
          <Input data-testid={`pm-section-prefix-${section.id}`} value={prefix} onChange={(e) => setPrefix(e.target.value)} onBlur={() => prefix !== (section.prefix || "") && onUpdate(section.id, { prefix })} placeholder="A-" className="h-8 w-20 border-zinc-800 bg-zinc-950 text-sm text-white" />
        </div>
        </>)}
        <span className="text-xs text-zinc-600">{perfs.length} performances</span>
        <button data-testid={`pm-section-delete-${section.id}`} onClick={onDelete} className="ml-auto text-zinc-600 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="mt-3">
        <PerfList perfs={perfs} sections={sections} fields={fields} flexible={flexible} locked={locked} view={view}
          onReorder={onReorderPerfs} onUpd={onUpdPerf} onDel={onDelPerf} onMove={onMovePerf} />
      </div>
      {view === "editor" && <AddPerf onAdd={(body) => onAddPerf(section.id, body)} />}
    </div>
  );
}

function PerfList({ perfs, sections, fields, flexible, locked, view, onReorder, onUpd, onDel, onMove }) {
  const Row = ({ p }) => (view === "files"
    ? <PerfFileRow p={p} onUpd={onUpd} />
    : view === "tracker"
    ? <PerfTrackerRow p={p} onUpd={onUpd} />
    : <PerfEditRow p={p} sections={sections} fields={fields} flexible={flexible} onUpd={onUpd} onDel={onDel} onMove={onMove} draggable={!locked} />);
  if (locked || view !== "editor") {
    return <div className="space-y-1.5">{perfs.map((p) => <div key={p.id} data-testid={`pm-perf-${p.id}`}><Row p={p} /></div>)}</div>;
  }
  return (
    <Reorder.Group axis="y" values={perfs} onReorder={onReorder} className="space-y-1.5">
      {perfs.map((p) => (
        <Reorder.Item key={p.id} value={p} data-testid={`pm-perf-${p.id}`}><Row p={p} /></Reorder.Item>
      ))}
    </Reorder.Group>
  );
}

const IndexBadge = ({ p }) => (
  <span data-testid={`pm-perf-index-${p.id}`} className="flex h-8 min-w-[2.5rem] shrink-0 items-center justify-center rounded-md bg-[#ff2a5f]/15 px-1 font-mono-x text-sm font-bold text-[#ff2a5f]">{p.index}</span>
);

function PerfEditRow({ p, sections, fields, flexible, onUpd, onDel, onMove, draggable }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-2">
      <div className="flex flex-wrap items-center gap-2">
        {draggable && <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-zinc-700" />}
        <IndexBadge p={p} />
        {fields.number?.enabled && (
          <Input data-testid={`pm-perf-number-${p.id}`} defaultValue={p.number} disabled={flexible} onBlur={(e) => !flexible && e.target.value !== p.number && onUpd(p.id, { number: e.target.value })} placeholder="No." className="h-7 w-16 border-zinc-800 bg-zinc-900 text-xs text-white disabled:opacity-60" />
        )}
        {fields.studio?.enabled && (
          <Input data-testid={`pm-perf-studio-${p.id}`} defaultValue={p.studio} onBlur={(e) => e.target.value !== p.studio && onUpd(p.id, { studio: e.target.value })} placeholder={fields.studio?.label || "Studio"} className="h-7 w-32 border-zinc-800 bg-zinc-900 text-xs text-white" />
        )}
        <Input data-testid={`pm-perf-name-${p.id}`} defaultValue={p.name} onBlur={(e) => e.target.value !== p.name && onUpd(p.id, { name: e.target.value })} placeholder={fields.name?.label || "Dancer / team"} className="h-7 min-w-[140px] flex-1 border-zinc-800 bg-zinc-900 text-xs text-white" />
        <Select value={p.section_id || "none"} onValueChange={(v) => onMove(p.id, v === "none" ? null : v)}>
          <SelectTrigger data-testid={`pm-perf-move-${p.id}`} className="h-7 w-32 border-zinc-800 bg-zinc-900 text-xs text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
            <SelectItem value="none">— Unassigned —</SelectItem>
            {sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <button data-testid={`pm-perf-delete-${p.id}`} onClick={() => onDel(p.id)} className="text-zinc-600 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      <NotesField p={p} onUpd={onUpd} />
    </div>
  );
}

function PerfTrackerRow({ p, onUpd }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-2">
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <IndexBadge p={p} />
        <span className="min-w-[120px] flex-1 truncate text-sm text-white">{p.label}</span>
        <label className="flex items-center gap-1.5 text-zinc-400">
          <Checkbox data-testid={`pm-perf-completed-${p.id}`} checked={!!p.completed} onCheckedChange={(c) => onUpd(p.id, { completed: !!c })} className="border-zinc-600" /> Completed
        </label>
        {p.completed && p.completed_at && <span data-testid={`pm-perf-completed-at-${p.id}`} className="inline-flex items-center gap-1 text-emerald-400"><Clock className="h-3 w-3" />{new Date(p.completed_at).toLocaleString()}</span>}
        <label className="flex items-center gap-1.5 text-zinc-400">Date
          <Input data-testid={`pm-perf-date-${p.id}`} type="date" defaultValue={p.performance_date || ""} onBlur={(e) => e.target.value !== (p.performance_date || "") && onUpd(p.id, { performance_date: e.target.value })} className="h-7 w-36 border-zinc-800 bg-zinc-900 text-xs text-white" />
        </label>
        <label className="flex items-center gap-1.5 text-zinc-400">
          <Checkbox data-testid={`pm-perf-photos-${p.id}`} checked={!!p.photos_captured} onCheckedChange={(c) => onUpd(p.id, { photos_captured: !!c })} className="border-zinc-600" /><Camera className="h-3 w-3" /> Photos
        </label>
        <label className="flex items-center gap-1.5 text-zinc-400">
          <Checkbox data-testid={`pm-perf-video-${p.id}`} checked={!!p.video_captured} onCheckedChange={(c) => onUpd(p.id, { video_captured: !!c })} className="border-zinc-600" /><Video className="h-3 w-3" /> Video
        </label>
      </div>
      <NotesField p={p} onUpd={onUpd} />
    </div>
  );
}

function PerfFileRow({ p, onUpd }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(p.filename); setCopied(true); toast.success("Filename copied"); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 p-2">
      <IndexBadge p={p} />
      <code data-testid={`pm-filename-${p.id}`} className="min-w-[200px] flex-1 break-all rounded bg-zinc-900 px-2 py-1.5 font-mono-x text-xs text-emerald-400">{p.filename || "—"}</code>
      <button data-testid={`pm-copy-${p.id}`} onClick={copy} className="inline-flex items-center gap-1 rounded-md border border-zinc-700 px-2.5 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800 hover:text-white">
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />} Copy
      </button>
      <Input data-testid={`pm-perf-notes-${p.id}`} defaultValue={p.notes || ""} onBlur={(e) => e.target.value !== (p.notes || "") && onUpd(p.id, { notes: e.target.value })} placeholder="Notes…" className="h-8 w-40 border-zinc-800 bg-zinc-900/60 text-xs text-zinc-300" />
    </div>
  );
}

function NotesField({ p, onUpd }) {
  return (
    <Textarea data-testid={`pm-perf-notes-${p.id}`} defaultValue={p.notes || ""} rows={1}
      onBlur={(e) => e.target.value !== (p.notes || "") && onUpd(p.id, { notes: e.target.value })}
      placeholder="Notes…" className="mt-2 min-h-[32px] border-zinc-800 bg-zinc-900/60 text-xs text-zinc-300" />
  );
}

function AddPerf({ onAdd }) {
  const [bulk, setBulk] = useState(false);
  const [val, setVal] = useState("");
  const [row, setRow] = useState({ name: "", studio: "", number: "" });
  if (bulk) {
    return (
      <div className="mt-2 space-y-2 rounded-lg border border-dashed border-zinc-700 p-2">
        <Label className="text-xs uppercase text-zinc-500">One dancer/team per line</Label>
        <textarea data-testid="pm-perf-bulk-input" value={val} onChange={(e) => setVal(e.target.value)} rows={3} placeholder={"Ava Smith\nTeam Phoenix"} className="w-full rounded-md border border-zinc-700 bg-zinc-950 p-2 text-sm text-white outline-none focus:border-zinc-500" />
        <div className="flex gap-2">
          <Button data-testid="pm-perf-bulk-save" onClick={() => { const arr = val.split("\n").map((s) => s.trim()).filter(Boolean); if (arr.length) onAdd({ lines: arr }); setVal(""); setBulk(false); }} className="h-8 bg-[#ff2a5f] text-white hover:bg-[#e01f50]">Add all</Button>
          <Button variant="ghost" onClick={() => setBulk(false)} className="h-8 text-zinc-400">Cancel</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Input data-testid="pm-add-perf-name" value={row.name} onChange={(e) => setRow({ ...row, name: e.target.value })} placeholder="Dancer / team" className="h-8 w-40 border-zinc-800 bg-zinc-950 text-sm text-white" />
      <Input data-testid="pm-add-perf-studio" value={row.studio} onChange={(e) => setRow({ ...row, studio: e.target.value })} placeholder="Studio" className="h-8 w-32 border-zinc-800 bg-zinc-950 text-sm text-white" />
      <Button data-testid="pm-add-perf" onClick={() => { if (!row.name.trim() && !row.studio.trim()) return; onAdd(row); setRow({ name: "", studio: "", number: "" }); }} variant="outline" className="h-8 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800"><Plus className="mr-1 h-3.5 w-3.5" /> Add performance</Button>
      <button data-testid="pm-perf-bulk-open" onClick={() => setBulk(true)} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-white"><ListPlus className="h-3.5 w-3.5" /> Bulk add</button>
    </div>
  );
}
